"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { getToken, onMessage } from "firebase/messaging";
import { getMessagingInstance, registerServiceWorker } from "./firebase";
import { registerFCMDevice, unregisterFCMDevice } from "../actions/notification.action";
import { getDeviceInfo } from "./device";
import {
  STORAGE_KEY_FCM_TOKEN,
  STORAGE_KEY_NOTIFICATION_PROMPT_DISMISSED,
  SEVEN_DAYS_MS,
  NOTIFICATION_PROMPT_DELAY_MS,
} from "@/constants/index";

const NotificationContext = createContext<NotificationContextType | null>(null);

// Helper to check if notifications are supported
const checkNotificationSupport = (): boolean => {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator
  );
};

// Helper to check if prompt was dismissed recently
const wasPromptDismissedRecently = (): boolean => {
  const dismissedAt = localStorage.getItem(STORAGE_KEY_NOTIFICATION_PROMPT_DISMISSED);
  if (!dismissedAt) return false;
  return Date.now() - parseInt(dismissedAt, 10) < SEVEN_DAYS_MS;
};

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [permission, setPermission] = useState<NotificationPermission>("default");
  const [isSupported, setIsSupported] = useState(false);
  const [isTokenRegistered, setIsTokenRegistered] = useState(false);
  const [shouldShowPrompt, setShouldShowPrompt] = useState(false);

  const [registrationTrigger, setRegistrationTrigger] = useState(0);
  const tokenRegistrationInProgress = useRef(false);
  const messageListeners = useRef<Set<(payload: NotificationPayload) => void>>(new Set());
  const unsubscribeRef = useRef<(() => void) | null>(null);

  const markAsInstalled = useCallback(() => {
    setIsTokenRegistered(true);
  }, []);

  // Initialize on mount
  useEffect(() => {
    const supported = checkNotificationSupport();
    setIsSupported(supported);

    if (!supported) return;

    // Set initial permission
    setPermission(Notification.permission);

    // Check if should show prompt
    if (Notification.permission === "default" && !wasPromptDismissedRecently()) {
      const timer = setTimeout(() => setShouldShowPrompt(true), NOTIFICATION_PROMPT_DELAY_MS);
      return () => clearTimeout(timer);
    }
  }, []);

  // Listen for permission changes via Permissions API
  useEffect(() => {
    if (!isSupported) return;

    let permissionStatus: PermissionStatus | null = null;

    const handlePermissionChange = () => {
      setPermission(Notification.permission);
    };

    navigator.permissions
      .query({ name: "notifications" })
      .then((status) => {
        permissionStatus = status;
        status.addEventListener("change", handlePermissionChange);
      })
      .catch(() => {
        // Fallback: no-op, permission state is updated when requestPermission is called
      });

    return () => {
      permissionStatus?.removeEventListener("change", handlePermissionChange);
    };
  }, [isSupported]);

  // Register FCM token when permission is granted
  useEffect(() => {
    if (permission !== "granted" || tokenRegistrationInProgress.current) return;

    const registerToken = async () => {
      tokenRegistrationInProgress.current = true;

      try {
        const messaging = getMessagingInstance();
        if (!messaging) {
          tokenRegistrationInProgress.current = false;
          return;
        }

        const swRegistration = await registerServiceWorker();
        const token = await getToken(messaging, {
          vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY,
          serviceWorkerRegistration: swRegistration || undefined,
        });

        if (!token) {
          tokenRegistrationInProgress.current = false;
          return;
        }

        // Check if token changed
        const storedToken = localStorage.getItem(STORAGE_KEY_FCM_TOKEN);
        if (storedToken === token) {
          setIsTokenRegistered(true);
          tokenRegistrationInProgress.current = false;
          return;
        }

        // Register with backend
        const { deviceType, deviceName } = getDeviceInfo();
        const result = await registerFCMDevice({
          deviceToken: token,
          deviceType,
          deviceName,
        });

        if (result?.success) {
          localStorage.setItem(STORAGE_KEY_FCM_TOKEN, token);
          setIsTokenRegistered(true);
        }
      } catch (error) {
        console.error("Error registering FCM token:", error);
      } finally {
        tokenRegistrationInProgress.current = false;
      }
    };

    registerToken();
  }, [permission, registrationTrigger]);

  // Set up foreground message listener when permission is granted
  useEffect(() => {
    if (permission !== "granted") return;

    const messaging = getMessagingInstance();
    if (!messaging) return;

    unsubscribeRef.current = onMessage(messaging, (payload) => {
      const normalizedPayload: NotificationPayload = {
        title: payload.notification?.title || payload.data?.title || "New Notification",
        body: payload.notification?.body || payload.data?.message || "",
        image: payload.notification?.image || payload.data?.image,
        icon: payload.notification?.icon || payload.data?.icon,
        navigateUrl: payload.data?.navigate_url || payload.fcmOptions?.link,
        data: payload.data,
      };

      // Notify all subscribers
      messageListeners.current.forEach((callback) => {
        try {
          callback(normalizedPayload);
        } catch (error) {
          console.error("Error in message listener callback:", error);
        }
      });

      // Dispatch global event for components that need to refresh
      window.dispatchEvent(new CustomEvent("notification-received"));
    });

    return () => {
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
    };
  }, [permission]);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isSupported) return false;

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      setShouldShowPrompt(false);
      localStorage.removeItem(STORAGE_KEY_NOTIFICATION_PROMPT_DISMISSED);
      return result === "granted";
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      return false;
    }
  }, [isSupported]);

  const dismissPrompt = useCallback(() => {
    setShouldShowPrompt(false);
    localStorage.setItem(STORAGE_KEY_NOTIFICATION_PROMPT_DISMISSED, Date.now().toString());
  }, []);

  const subscribeToMessages = useCallback(
    (callback: (payload: NotificationPayload) => void): (() => void) => {
      messageListeners.current.add(callback);
      return () => {
        messageListeners.current.delete(callback);
      };
    },
    []
  );

  const clearToken = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY_FCM_TOKEN);
    setIsTokenRegistered(false);
  }, []);

  const signOutCleanup = useCallback(async () => {
    try {
      let tokenToUnregister = localStorage.getItem(STORAGE_KEY_FCM_TOKEN);

      // Fallback: get token directly from Firebase SDK if not in localStorage
      if (!tokenToUnregister) {
        const messaging = getMessagingInstance();
        if (messaging) {
          const swReg = await navigator.serviceWorker
            ?.getRegistration("/firebase-messaging-sw.js")
            .catch(() => undefined);
          tokenToUnregister = await getToken(messaging, {
            vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY,
            serviceWorkerRegistration: swReg,
          }).catch(() => null);
        }
      }

      if (tokenToUnregister) {
        await unregisterFCMDevice(tokenToUnregister);
      } else {
        console.warn("signOutCleanup: no FCM token found to unregister");
      }
    } catch (error) {
      console.error("signOutCleanup error:", error);
    } finally {
      localStorage.removeItem(STORAGE_KEY_FCM_TOKEN);
      tokenRegistrationInProgress.current = false;
      setIsTokenRegistered(false);
    }
  }, []);

  const triggerReRegistration = useCallback(() => {
    tokenRegistrationInProgress.current = false;
    console.log("Triggering re-registration of FCM token");
    setRegistrationTrigger((prev) => prev + 1);
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        permission,
        isSupported,
        isTokenRegistered,
        shouldShowPrompt,
        requestPermission,
        dismissPrompt,
        subscribeToMessages,
        clearToken,
        signOutCleanup,
        triggerReRegistration,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotification(): NotificationContextType {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification must be used within NotificationProvider");
  }
  return context;
}

// Legacy export for backwards compatibility during migration
export function clearStoredFCMToken() {
  if (typeof window !== "undefined") {
    localStorage.removeItem(STORAGE_KEY_FCM_TOKEN);
  }
}
